import React, { FC, useEffect } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { WHITE, BLACK } from '../../../constants/theme';
import ClipItemLeft from './ClipItemLeft';
import ClipItemMiddle from './ClipItemMiddle';
import ClipItemRight from './ClipItemRight';
import { Colors } from '../../../../../constants';
import { selectIsClipEditing, resetCurrentEditingClipIndex } from '../../../state/currentEditingClip';
import { VIDEO_CONTENT_ID } from '../../VideoPlayerPanel/VideoContent';
import { IAppState } from '../../../../../state/reducers';
import { IVideoClip } from '../../../../../api/vault/clipList';

// Utility
function assertIsNode(e: EventTarget | null): asserts e is Node {
  if (!e || !('nodeType' in e)) {
    throw new Error('Node expected');
  }
}

interface ClipItemProps {
  $isEditing?: boolean;
  $isCurrentPlaying?: boolean;
  $isExchangeLiveEvent?: boolean;
}

const ClipItemContainer = styled.div<ClipItemProps>`
  height: ${(props) => (props.$isExchangeLiveEvent ? '82px' : '60px')};
  min-height: ${(props) => (props.$isExchangeLiveEvent ? '82px' : '60px')};
  width: 99%;
  display: flex;
  padding: 5px 0;
  margin-bottom: 10px;
  background: ${(props) => (props.$isEditing ? Colors.PRIMARY_3 : WHITE)};
  border: ${(props) => props.$isCurrentPlaying && `0.5px solid ${BLACK}`};
  border-radius: 4px;
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.16);

  &:hover {
    border: 0.5px solid ${BLACK};
  }
`;

interface IOwnProps {
  id: string;
  innerRef: () => void;
  provided: any;
  clipIndex: number;
  clip: IVideoClip;
}

interface IStateProps {
  currentPlayingIndex: number;
  currentReviewingIndex: number;
  isEditing: boolean;
  isExchangeLiveEvent: boolean;
}

interface IDispatchProps {
  resetCurrentEditingClipIndex: () => void;
}

interface IProps extends IOwnProps, IStateProps, IDispatchProps { }

const ClipItem: FC<IProps> = (props: IProps) => {
  const {
    id,
    innerRef,
    provided,
    clipIndex,
    currentPlayingIndex,
    currentReviewingIndex,
    isEditing,
    clip,
    isExchangeLiveEvent,
  } = props;

  const {
    markInTime,
    markOutTime,
  } = clip;

  const resetEditingOnClickOutside = (ev: MouseEvent) => {
    const {
      resetCurrentEditingClipIndex,
    } = props;
    const isClickOutsideItselfOrVideoPlayer = () => {
      assertIsNode(ev.target);
      return !document.getElementById(id).contains(ev.target)
        && !document.getElementById(VIDEO_CONTENT_ID).contains(ev.target);
    };

    if (isClickOutsideItselfOrVideoPlayer()) {
      resetCurrentEditingClipIndex();
    }
  };

  const addListenerToResetEditing = () => document.addEventListener('click', resetEditingOnClickOutside as unknown as EventListener);

  const removeListenerToResetEditing = () => document.removeEventListener('click', resetEditingOnClickOutside as unknown as EventListener);

  useEffect(() => {
    if (isEditing) addListenerToResetEditing();
    return () => {
      removeListenerToResetEditing();
    };
  }, [isEditing]);

  const digitsAfterDecimal = 2;
  const duration = (markOutTime - markInTime).toFixed(digitsAfterDecimal);
  const formattedDuration = markOutTime - markInTime > 0 ? duration : 'N/A';
  const isCurrentPlaying = (currentPlayingIndex !== null && currentPlayingIndex === clipIndex) || (currentReviewingIndex !== null && currentReviewingIndex === clipIndex);

  return (
    <ClipItemContainer
      id={id}
      ref={innerRef}
      $isCurrentPlaying={isCurrentPlaying}
      $isEditing={isEditing}
      $isExchangeLiveEvent={isExchangeLiveEvent}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
    >
      <ClipItemLeft clip={clip} duration={formattedDuration} clipIndex={clipIndex} />
      <ClipItemMiddle clip={clip} clipIndex={clipIndex} />
      <ClipItemRight clip={clip} clipIndex={clipIndex} />
    </ClipItemContainer>
  );
};

const mapStateToProps = (state: IAppState, ownProps: IOwnProps): IStateProps => ({
  currentPlayingIndex: state.currentPlayingClip.index,
  currentReviewingIndex: state.currentReviewingClip.index,
  isEditing: selectIsClipEditing(state, ownProps.clipIndex),
  isExchangeLiveEvent: state.clipperMediaContent.isExchangeLiveEvent,
});

const mapDispatchToProps: IDispatchProps = {
  resetCurrentEditingClipIndex,
};

export default connect(mapStateToProps, mapDispatchToProps)(ClipItem);
